import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import type { FeederSubTab } from '../shared';
import { TendenciasSubTab } from '../TendenciasSubTab';
import { FeederFiltersPanel } from '../panels/FeederFiltersPanel';
import { ManualSubTab } from '../feeder/ManualSubTab';
import { CategoriesSubTab } from '../feeder/CategoriesSubTab';
import { DiscoverSubTab } from '../feeder/DiscoverSubTab';
import { ScoutsSubTab } from '../feeder/ScoutsSubTab';
import { ManualPublishSubTab } from '../feeder/ManualPublishSubTab';
import { useCan } from '../../../lib/rbac';

function CategoryPickerModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string, name: string, path: string) => void;
}) {
  const { sellerId } = useUIStore();
  const [tab, setTab] = useState<'search' | 'tree'>('search');
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [treeData, setTreeData] = useState<any>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [childrenMap, setChildrenMap] = useState<Record<string, any[]>>({});
  const debRef = useRef<any>(null);

  if (!open) return null;

  const doSearch = async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const cats = Array.isArray(res.data) ? res.data : res.data.categories || [];
        setResults(cats.filter((c: any) =>
          (c.name || '').toLowerCase().includes(q.toLowerCase())
        ).slice(0, 15));
      }
    } catch {
      // ignore
    } finally {
      setSearching(false);
    }
  };

  const onSearchInput = (v: string) => {
    setSearchQ(v);
    if (debRef.current) clearTimeout(debRef.current);
    if (v.length < 2) { setResults([]); return; }
    debRef.current = setTimeout(() => doSearch(v), 300);
  };

  const loadTreeRoot = async () => {
    if (treeData) return;
    try {
      const res = await api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setTreeData({ children: Array.isArray(res.data) ? res.data : res.data.categories || [] });
      }
    } catch {
      // ignore
    }
  };

  const loadChildren = async (id: string) => {
    if (childrenMap[id]) { setExpanded(prev => ({ ...prev, [id]: true })); return; }
    try {
      const res = await api.get<any>(`/publisher/discover/categories/${id}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const ch = Array.isArray(res.data) ? res.data : res.data.children || [];
        setChildrenMap(prev => ({ ...prev, [id]: ch }));
        setExpanded(prev => ({ ...prev, [id]: true }));
      }
    } catch {
      // ignore
    }
  };

  const toggleTreeNode = (id: string) => {
    if (expanded[id]) setExpanded(prev => { const n = { ...prev }; delete n[id]; return n; });
    else loadChildren(id);
  };

  const renderTreeNode = (node: any, depth: number): React.ReactNode => {
    const id = String(node.catId || node.id || node.category_id);
    const hasKids = (node.items || node.childrenCount || node.children_count || 0) > 0 || (childrenMap[id]?.length > 0);
    const isExp = expanded[id];
    const isSel = selected?.id === id;

    return (
      <div key={id}>
        <div
          className={`flex items-center gap-1.5 py-1.5 px-2 rounded text-xs cursor-pointer hover:bg-[var(--color-bg-tertiary)] ${isSel ? 'bg-blue-900/30 text-blue-400' : 'text-[var(--color-text)]'}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => { if (hasKids) toggleTreeNode(id); setSelected({ id, name: node.name || node.category_name, path: '' }); }}
        >
          {hasKids ? (
            <span className="text-[var(--color-text-tertiary)] w-4 text-center text-[10px]">{isExp ? '\u25BC' : '\u25B6'}</span>
          ) : (
            <span className="w-4" />
          )}
          <span>{node.name || node.category_name}</span>
        </div>
        {isExp && childrenMap[id]?.map(c => renderTreeNode(c, depth + 1))}
      </div>
    );
  };

  // Load tree when switching to tree tab
  if (tab === 'tree' && !treeData) loadTreeRoot();

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-5 rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[var(--color-text)]">Select CBT Category</h3>
          <button onClick={onClose} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] text-xl">{'\u00D7'}</button>
        </div>
        <div className="flex gap-1 mb-3">
          <button
            onClick={() => setTab('search')}
            className={`flex-1 px-3 py-1.5 rounded text-xs font-medium ${tab === 'search' ? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]' : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]'}`}
          >
            Search
          </button>
          <button
            onClick={() => setTab('tree')}
            className={`flex-1 px-3 py-1.5 rounded text-xs font-medium ${tab === 'tree' ? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]' : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]'}`}
          >
            Tree
          </button>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {tab === 'search' ? (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search CBT categories..."
                value={searchQ}
                onChange={e => onSearchInput(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
              {searching ? (
                <div className="text-center py-4 text-xs text-[var(--color-text-tertiary)]">Searching...</div>
              ) : results.length === 0 ? (
                <div className="text-center py-4 text-xs text-[var(--color-text-tertiary)]">{searchQ.length >= 2 ? 'No results' : 'Type 2+ characters'}</div>
              ) : (
                <div className="space-y-0.5">
                  {results.map(r => {
                    const id = String(r.catId || r.id || r.category_id);
                    const isSel = selected?.id === id;
                    return (
                      <div
                        key={id}
                        className={`py-2 px-3 rounded cursor-pointer hover:bg-[var(--color-bg-tertiary)] ${isSel ? 'bg-blue-900/30 ring-1 ring-blue-500/40' : ''}`}
                        onClick={() => setSelected({ id, name: r.name || r.category_name, path: r.path_from_root || '' })}
                      >
                        <div className="text-xs text-[var(--color-text)] font-medium">{r.name || r.category_name}</div>
                        {r.path_from_root && <div className="text-[10px] text-[var(--color-text-tertiary)] font-mono truncate">{r.path_from_root}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              {!treeData ? (
                <div className="text-center py-4 text-xs text-[var(--color-text-tertiary)]">Loading tree...</div>
              ) : (treeData.children || []).length === 0 ? (
                <div className="text-center py-4 text-xs text-[var(--color-text-tertiary)]">No children</div>
              ) : (
                (treeData.children || []).map((c: any) => renderTreeNode(c, 0))
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-[var(--color-border)]">
          <button onClick={onClose} className="px-4 py-1.5 bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] rounded-lg text-xs font-medium border border-[var(--color-border)]">Cancel</button>
          <button
            disabled={!selected}
            onClick={() => { if (selected) onSelect(selected.id, selected.name, selected.path || ''); onClose(); }}
            className="px-4 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-xs font-medium disabled:opacity-40"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
}

function CurationView({
  asins: inputAsins,
  sellerId,
  sites,
  filters,
  condition = 'new',
  onResult,
  onBack,
}: {
  asins: string[];
  sellerId: number;
  sites: string[];
  filters: any;
  condition?: 'new' | 'refurbished';
  onResult: (r: any) => void;
  onBack: () => void;
}) {
  const [groups, setGroups] = useState<Map<string, any>>(new Map());
  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [phase, setPhase] = useState('init');
  const [products, setProducts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const can = useCan();
  const canMoney = can('global.view_money');

  // Fetch preview data for all asins
  useEffect(() => {
    if (!inputAsins.length) return;
    setPhase('fetching');
    const BATCH = 200;
    const batches: string[][] = [];
    for (let i = 0; i < inputAsins.length; i += BATCH) batches.push(inputAsins.slice(i, i + BATCH));

    Promise.all(batches.map(batch =>
      api.post<any>('/publisher/discover/preview', { asins: batch, seller_id: sellerId })
    )).then(results => {
      const prods = results.flatMap(r => {
        if (!r.success) return [];
        return Array.isArray(r.data) ? r.data : r.data?.products || [];
      });
      const st = {
        total: prods.length,
        from_cache: results.reduce((s, r) => s + ((r as any).stats?.from_cache || 0), 0),
        fetched: results.reduce((s, r) => s + ((r as any).stats?.fetched || 0), 0),
      };
      setProducts(prods);
      setStats(st);
    }).catch(err => {
      console.error('Preview failed:', err);
      onResult({ success: false, error: 'Failed to fetch preview data' });
      setLoading(false);
    });
  }, [inputAsins, sellerId]);

  // Process products into groups
  useEffect(() => {
    if (!products.length) return;
    setPhase('grouping');
    const gMap = new Map<string, any>();
    products.forEach(prod => {
      const tree = prod.amazon_category_tree || [];
      const key = tree.length > 0 ? tree.map((n: any) => n.name || n).join(' > ') : 'Uncategorized';
      const leaf = tree.length > 0 ? (tree[tree.length - 1].name || tree[tree.length - 1]) : 'Uncategorized';
      if (!gMap.has(key)) gMap.set(key, { key, leaf, products: [], cbt: null, alternatives: [], sites: null });
      gMap.get(key)!.products.push(prod);
    });
    setGroups(gMap);

    // Auto-select products that pass filters
    const sel = new Set<string>();
    products.forEach(prod => {
      if (prod.existing_status) return;
      if (!prod.ml_price || prod.ml_price_error) return;
      sel.add(prod.asin);
    });
    setSelectedAsins(sel);

    // Categorize groups
    setPhase('categorizing');
    const groupArr = Array.from(gMap.entries()).map(([key, g]) => ({ key, leaf: g.leaf }));
    api.post<any>('/publisher/discover/categorize', { groups: groupArr })
      .then(res => {
        if (!res.success) throw new Error('Categorization failed');
        const catResults = (res.data as any)?.groups || res.data || [];
        setGroups(prev => {
          const next = new Map(prev);
          catResults.forEach((cr: any) => {
            const g = next.get(cr.group_key);
            if (g) {
              g.cbt = cr.suggested;
              g.alternatives = cr.alternatives || [];
              next.set(cr.group_key, { ...g });
            }
          });
          return next;
        });

        // Check sites for each group
        setPhase('checking_sites');
        const sitePromises = catResults
          .filter((cr: any) => cr.suggested)
          .map((cr: any) =>
            api.post<any>('/publisher/check-sites', { category_id: cr.suggested.id, category_name: cr.suggested.name, seller_id: sellerId })
              .then(sr => ({ key: cr.group_key, sites: sr.success !== false ? sr : sr.data }))
              .catch(() => ({ key: cr.group_key, sites: null }))
          );
        return Promise.all(sitePromises);
      })
      .then(siteResults => {
        setGroups(prev => {
          const next = new Map(prev);
          (siteResults || []).forEach((sr: any) => {
            const g = next.get(sr.key);
            if (g && sr.sites) {
              g.sites = sr.sites;
              next.set(sr.key, { ...g });
            }
          });
          return next;
        });
        setLoading(false);
        setPhase('ready');
      })
      .catch(() => { setLoading(false); setPhase('ready'); });
  }, [products, sellerId]);

  const toggleAsin = (asin: string) => setSelectedAsins(prev => { const n = new Set(prev); if (n.has(asin)) n.delete(asin); else n.add(asin); return n; });

  const handleCategoryChange = (groupKey: string, id: string, name: string) => {
    setGroups(prev => {
      const next = new Map(prev);
      const g = next.get(groupKey);
      if (g) { g.cbt = { id, name, confidence: 100 }; next.set(groupKey, { ...g }); }
      return next;
    });
    setEditingGroup(null);
    // Re-check sites
    api.post<any>('/publisher/check-sites', { category_id: id, category_name: name, seller_id: sellerId })
      .then(sr => {
        setGroups(prev => {
          const next = new Map(prev);
          const g = next.get(groupKey);
          if (g) { g.sites = sr.success !== false ? sr : sr.data; next.set(groupKey, { ...g }); }
          return next;
        });
      }).catch(() => {});
  };

  const doConfirm = async () => {
    if (selectedAsins.size === 0) return;
    setConfirming(true);
    const items: any[] = [];
    groups.forEach(g => {
      g.products.forEach((prod: any) => {
        if (!selectedAsins.has(prod.asin)) return;
        items.push({
          asin: prod.asin,
          title: prod.title,
          override_category_id: g.cbt?.id || null,
          override_category_name: g.cbt?.name || null,
          allowed_sites: g.sites?.allowed_sites || null,
          blocked_sites: g.sites?.blocked_sites || null,
        });
      });
    });
    try {
      const res = await api.post<any>('/publisher/curate/confirm', { items, seller_id: sellerId, sites, condition });
      onResult(res.success !== false ? res : res.data);
      onBack();
    } catch (err: any) {
      onResult({ success: false, error: err.message });
    } finally {
      setConfirming(false);
    }
  };

  const phaseLabel: Record<string, string> = {
    init: 'Initializing...', fetching: 'Fetching preview...', grouping: 'Grouping products...',
    categorizing: 'Matching CBT categories...', checking_sites: 'Checking site eligibility...', ready: '',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)]">Curating {products.length} products</h3>
          <span className="text-[10px] text-[var(--color-text-tertiary)]">{stats.from_cache || 0} cached, {stats.fetched || 0} fetched</span>
          {phase !== 'ready' && <span className="text-[10px] text-blue-400 ml-2">{phaseLabel[phase] || ''}</span>}
        </div>
        <button onClick={onBack} className="px-3 py-1.5 bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] rounded-lg text-xs font-medium border border-[var(--color-border)] hover:text-[var(--color-text)]">{'\u2190'} Back</button>
      </div>

      <div className="flex gap-3 items-center">
        <button onClick={() => { const n = new Set<string>(); products.forEach(p => n.add(p.asin)); setSelectedAsins(n); }} className="text-xs text-[var(--color-primary)] hover:underline">Select All</button>
        <button onClick={() => setSelectedAsins(new Set())} className="text-xs text-[var(--color-primary)] hover:underline">Deselect All</button>
        <span className="text-xs text-[var(--color-text-tertiary)] ml-auto">{selectedAsins.size} selected</span>
      </div>

      {loading && phase !== 'ready' && (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
      )}

      {Array.from(groups.entries()).map(([gKey, g]) => (
        <div key={gKey} className="bg-[var(--color-bg-secondary)] rounded-xl border border-[var(--color-border)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm">{'\u{1F4C1}'}</span>
              <span className="text-xs font-semibold text-[var(--color-text)] truncate">{gKey}</span>
              <span className="text-[10px] text-[var(--color-text-tertiary)]">({g.products.length})</span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[var(--color-text-tertiary)]">CBT:</span>
                {g.cbt ? (
                  <><span className="text-xs text-emerald-400 font-medium">{g.cbt.name}</span><span className="text-[10px] text-[var(--color-text-tertiary)]">({g.cbt.confidence}%)</span></>
                ) : (
                  <span className="text-xs text-[var(--color-text-tertiary)] italic">No match</span>
                )}
                <button onClick={() => setEditingGroup(gKey)} className="px-1.5 py-0.5 bg-blue-600/15 text-blue-400 rounded text-[10px] font-medium hover:bg-blue-600/25">Change</button>
              </div>
              {g.sites && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-[var(--color-text-tertiary)]">Publish:</span>
                  {(sites || []).map(s => {
                    const blocked = (g.sites.blocked_sites || []).find((b: any) => b.site === s);
                    const allowed = (g.sites.allowed_sites || []).includes(s);
                    return (
                      <span
                        key={s}
                        className={`px-1 py-0.5 rounded text-[9px] font-mono border ${
                          blocked ? 'bg-red-900/30 text-red-400 border-red-700/30' :
                          allowed ? 'bg-green-900/30 text-green-400 border-green-700/30' :
                          'bg-yellow-900/30 text-yellow-400 border-yellow-700/30'
                        }`}
                        title={blocked?.reason || (!allowed ? 'Not in allowed sites' : '')}
                      >
                        {s}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-[var(--color-text-tertiary)] uppercase tracking-wider border-b border-[var(--color-border)]">
                  <th className="p-2 w-8" /><th className="p-2 text-left">Title</th><th className="p-2 text-right">$AMZ</th><th className="p-2 text-right">$ML</th><th className="p-2 text-right">BSR</th>
                </tr>
              </thead>
              <tbody>
                {g.products.map((prod: any) => {
                  const sel = selectedAsins.has(prod.asin);
                  return (
                    <tr key={prod.asin} className={`border-b border-[var(--color-border)] cursor-pointer ${sel ? '' : 'opacity-50'}`} onClick={() => toggleAsin(prod.asin)}>
                      <td className="p-2"><input type="checkbox" checked={sel} onChange={() => toggleAsin(prod.asin)} onClick={e => e.stopPropagation()} /></td>
                      <td className="p-2 text-left">
                        <span className="text-[var(--color-text)] truncate block max-w-[300px]">{(prod.title || '').substring(0, 50) || '\u2014'}</span>
                        <span className="text-[10px] text-[var(--color-text-tertiary)] font-mono">{prod.asin}</span>
                      </td>
                      <td className="p-2 text-right font-mono text-[var(--color-text)]">{canMoney && prod.price ? `$${prod.price.toFixed(2)}` : '\u2014'}</td>
                      <td className="p-2 text-right font-mono">{canMoney && prod.ml_price ? <span className="text-green-400">${prod.ml_price.toFixed(2)}</span> : <span className="text-red-400">{'\u2014'}</span>}</td>
                      <td className="p-2 text-right font-mono text-[var(--color-text-tertiary)]">{prod.bsr ? `#${prod.bsr.toLocaleString()}` : '\u2014'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="flex justify-between items-center pt-2">
        <button onClick={onBack} className="px-4 py-2 bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] rounded-lg text-sm font-medium border border-[var(--color-border)] hover:text-[var(--color-text)]">Cancel</button>
        {can('publisher.enqueue') && (
        <button
          onClick={doConfirm}
          disabled={selectedAsins.size === 0 || confirming}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {confirming ? 'Queueing...' : `Queue ${selectedAsins.size} Selected \u2192`}
        </button>
        )}
      </div>

      <CategoryPickerModal
        open={editingGroup !== null}
        onClose={() => setEditingGroup(null)}
        onSelect={(id, name) => { if (editingGroup) handleCategoryChange(editingGroup, id, name); }}
      />
    </div>
  );
}

function FeederTab() {
  const { t } = useTranslation();
  const can = useCan();
  const [subTab, setSubTab] = useState<FeederSubTab>('manual');
  const { sellerId, sellers } = useUIStore();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [feederFilters, setFeederFilters] = useState<any>(null);
  const [selCat, setSelCat] = useState<any>(null);
  const [quickMode, setQuickMode] = useState(() => localStorage.getItem('cbt_feeder_quick_mode') === 'true');
  const [curationAsins, setCurationAsins] = useState<string[] | null>(null);
  const [result, setResult] = useState<any>(null);
  // Condición a publicar — NO persistente: arranca en 'new' cada vez para no
  // encolar refurbished por accidente (la política por defecto es solo NUEVOS).
  const [enqueueCondition, setEnqueueCondition] = useState<'new' | 'refurbished'>('new');

  // Load feeder filters once — shared with sub-tabs
  useEffect(() => {
    api.get<any>(`/publisher/filters?seller_id=${sellerId}`).then(res => {
      if (res.success && res.data) setFeederFilters(res.data.filters || {});
    }).catch(() => {});
  }, [sellerId]);

  const maxAsins = parseInt(feederFilters?.maxAsins) || 50;

  // Source of truth: sellers.active_sites (lo que el seller realmente puede publicar).
  // Si cbt_feeder_filters.sites tiene un sub-conjunto explícito, lo usamos pero siempre
  // intersectado con active_sites para no propagar sites no autorizados.
  const sellerActiveSites: string[] = sellers.find(s => s.id === sellerId)?.active_sites || [];
  const filtersSites: string[] = Array.isArray(feederFilters?.sites) ? feederFilters.sites : [];
  const selectedSites: string[] = filtersSites.length > 0
    ? filtersSites.filter(s => sellerActiveSites.includes(s))
    : sellerActiveSites;

  const toggleQuickMode = () => {
    setQuickMode(prev => {
      const nv = !prev;
      localStorage.setItem('cbt_feeder_quick_mode', nv ? 'true' : 'false');
      return nv;
    });
  };

  // Category select from Categories sub-tab → auto-switch to Discover
  const handleCatSelect = (cat: any) => {
    setSelCat(cat);
    setSubTab('discover');
  };

  const handleResult = (r: any) => {
    setResult(r);
    setTimeout(() => setResult(null), 8000);
  };

  const startCuration = (asins: string[]) => {
    setCurationAsins(asins);
  };

  // Cada sub-pestaña del descubrimiento pide su capacidad.
  const subTabs: { key: FeederSubTab; label: string; icon: React.ReactNode; cap: string }[] = ([
    { key: 'manual', label: t('pub.feed.manual'), icon: <PenTool size={14} />, cap: 'publisher.enqueue' },
    { key: 'categories', label: 'Categories', icon: <FolderTree size={14} />, cap: 'publisher.enqueue' },
    { key: 'discover', label: 'Discover', icon: <Star size={14} />, cap: 'publisher.enqueue' },
    { key: 'scouts', label: t('pub.feed.scouts'), icon: <Binoculars size={14} />, cap: 'publisher.scouts' },
    { key: 'tendencias', label: t('tendencias.title'), icon: <TrendingUp size={14} />, cap: 'publisher.enqueue' },
    { key: 'publish', label: t('pub.feed.publish'), icon: <Rocket size={14} />, cap: 'publisher.publish' },
  ] as { key: FeederSubTab; label: string; icon: React.ReactNode; cap: string }[]).filter(st => can(st.cap));
  // Sub-pestaña efectiva: la elegida si está permitida; si no, la primera permitida.
  const activeSub: FeederSubTab | null = subTabs.some(st => st.key === subTab)
    ? subTab
    : (subTabs[0]?.key ?? null);
  const canEnqueue = can('publisher.enqueue');

  // If curation view is active
  if (curationAsins) {
    return (
      <div className="space-y-4">
        <FeederFiltersPanel open={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} onFiltersChange={setFeederFilters} />
        {/* Result banner */}
        {result && (
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs border ${
            result.success !== false
              ? 'bg-green-900/10 border-green-700/20 text-green-400'
              : 'bg-red-900/10 border-red-700/20 text-red-400'
          }`}>
            <span className="text-base">{result.success !== false ? '\u2713' : '\u2715'}</span>
            <div className="flex-1">
              {result.success !== false
                ? <span><strong>{result.queued || 0} queued</strong></span>
                : result.error}
            </div>
            <button onClick={() => setResult(null)} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] text-lg">{'\u00D7'}</button>
          </div>
        )}
        <CurationView
          asins={curationAsins}
          sellerId={sellerId}
          sites={selectedSites}
          filters={feederFilters}
          condition={enqueueCondition}
          onResult={handleResult}
          onBack={() => setCurationAsins(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      <FeederFiltersPanel open={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} onFiltersChange={setFeederFilters} />

      {/* Result banner */}
      {result && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs border ${
          result.success !== false
            ? 'bg-green-900/10 border-green-700/20 text-green-400'
            : 'bg-red-900/10 border-red-700/20 text-red-400'
        }`}>
          <span className="text-base">{result.success !== false ? '\u2713' : '\u2715'}</span>
          <div className="flex-1">
            {result.success !== false
              ? <span><strong>{result.queued || 0} queued</strong>{result.enricher_triggered && <span className="text-[var(--color-text-tertiary)]"> · enricher triggered</span>}</span>
              : result.error}
          </div>
          <button onClick={() => setResult(null)} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] text-lg">{'\u00D7'}</button>
        </div>
      )}

      {/* Sub-tab bar with Quick Mode toggle */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {subTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setSubTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
              activeSub === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
        {/* Quick Mode toggle */}
        {canEnqueue && (
        <div className="flex items-center gap-1.5 px-3 flex-shrink-0 border-l border-[var(--color-border)] ml-1">
          <span className="text-[10px] text-[var(--color-text-tertiary)]">Quick</span>
          <button
            onClick={toggleQuickMode}
            className={`w-8 h-4 rounded-full transition-colors relative ${quickMode ? 'bg-amber-500' : 'bg-[var(--color-border)]'}`}
            title={quickMode ? 'Quick Mode ON: queue directly' : 'Quick Mode OFF: curate first'}
          >
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${quickMode ? 'left-[18px]' : 'left-0.5'}`} />
          </button>
        </div>
        )}
        {/* Condición a publicar — arranca en Nuevo cada vez (no se persiste) */}
        {canEnqueue && (
        <div className="flex items-center gap-1 px-3 flex-shrink-0 border-l border-[var(--color-border)]">
          <span className="text-[10px] text-[var(--color-text-tertiary)]">{t('pub.feed.condition')}</span>
          <div className="flex rounded-md overflow-hidden border border-[var(--color-border)]">
            <button
              onClick={() => setEnqueueCondition('new')}
              className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                enqueueCondition === 'new'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
              }`}
            >
              {t('pub.feed.conditionNew')}
            </button>
            <button
              onClick={() => setEnqueueCondition('refurbished')}
              className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                enqueueCondition === 'refurbished'
                  ? 'bg-amber-500 text-white'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
              }`}
            >
              {t('pub.feed.conditionRefurbished')}
            </button>
          </div>
        </div>
        )}
      </div>
      {/* Aviso visible cuando se va a encolar como reacondicionado */}
      {enqueueCondition === 'refurbished' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-amber-900/15 border border-amber-700/30 text-amber-400">
          <span className="text-sm">⚠</span>
          <span>{t('pub.feed.conditionRefurbishedWarning')}</span>
        </div>
      )}

      {/* Sub-tab content */}
      <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl p-4">
        {activeSub === 'manual' && <ManualSubTab sites={selectedSites} condition={enqueueCondition} />}
        {activeSub === 'categories' && <CategoriesSubTab onSelect={handleCatSelect} selectedCat={selCat} />}
        {activeSub === 'discover' && (
          <DiscoverSubTab
            category={selCat}
            filters={feederFilters}
            maxAsins={maxAsins}
            sites={selectedSites}
            quickMode={quickMode}
            onCurate={startCuration}
            condition={enqueueCondition}
          />
        )}
        {activeSub === null && (
          <p className="text-sm text-[var(--color-text-tertiary)] text-center py-6">
            Tu rol no tiene acciones de descubrimiento habilitadas.
          </p>
        )}
        {activeSub === 'scouts' && <ScoutsSubTab sites={selectedSites} feederFilters={feederFilters} />}
        {activeSub === 'tendencias' && <TendenciasSubTab sites={selectedSites} filters={feederFilters} />}
        {activeSub === 'publish' && <ManualPublishSubTab />}
      </div>
    </div>
  );
}

export { FeederTab };
